import {NoticeMessage} from '#/atoms/notice-message';
import {NoticeMessageErrorDetails} from '#/atoms/notice-message-error-details';
import {useWalletTransferCompletedStepOnSuccess} from '#/components/wallet-transfer/use-wallet-transfer-completed-step-on-success';
import {useWalletTransferTokenInputHandlers} from '#/components/wallet-transfer/use-wallet-transfer-token-input-handlers';
import {WalletTransferContext} from '#/components/wallet-transfer/wallet-transfer-context';
import {
  StepHeader,
  Title,
  TokenTransferSingleStepButton,
  TokenTransferStatusContext,
  TokenTransferStatusProvider,
  TokenTransferStatusTitle,
} from '#/components/wallet-transfer/wallet-transfer-modal';
import {WalletTransferTab} from '#/components/wallet-transfer/wallet-transfer-tab';
import {WalletTransferTokenInputGroup} from '#/components/wallet-transfer/wallet-transfer-token-input-group';
import {WalletContext} from '#/contexts/wallet-provider';
import {useNativeTokenBalance} from '#/hooks/query/use-native-token-balance';
import {getIsAddressValid} from '#/utils/account-utils';
import {tokenAmountToInputString} from '#/utils/token-string-utils';
import {wagmiConfig} from '#/wagmi/wagmi-config';
import {useMutation} from '@tanstack/react-query';
import {FunctionComponent, useContext, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {parseEther, parseGwei} from 'viem';
import {sendTransaction, waitForTransactionReceipt} from 'wagmi/actions';

const nativeBalanceMultiplier = 10n ** 12n;
export const WalletTransferStepNative: FunctionComponent = () => {
  const {completedTabs, address} = useContext(WalletTransferContext);
  const {
    accountResult: {address: account, chain},
  } = useContext(WalletContext);

  const {t} = useTranslation('translation');

  const {data: nativeTokenBalance, refetch: refetchNativeTokenBalance} = useNativeTokenBalance();

  const getInitialInputValue = () => tokenAmountToInputString(Number(nativeTokenBalance / nativeBalanceMultiplier));
  const {inputValue, onInputChange, onMaxButtonClick, inputAboveBalance} = useWalletTransferTokenInputHandlers(
    nativeTokenBalance,
    getInitialInputValue,
  );

  const transferEnabled = !!account && getIsAddressValid(address) && nativeTokenBalance !== -1n && !!account;
  const transferMutation = useMutation({
    mutationKey: ['transfer-native'],
    mutationFn: async () => {
      if (!transferEnabled) {
        throw new Error('Transfer Native is not enabled');
      }

      const sendTransactionCost = 21000n;
      const maxFeePerGas = parseGwei('20');
      const feeCost = sendTransactionCost * maxFeePerGas;
      const amountToSend = parseEther(inputValue);
      const value = amountToSend - feeCost;

      const hash = await sendTransaction(wagmiConfig, {
        to: address,
        value,
        maxFeePerGas,
      });

      const transactionReceipt = await waitForTransactionReceipt(wagmiConfig, {
        hash,
      });

      return transactionReceipt;
    },
  });

  const skippedSections = Object.values(WalletTransferTab).filter(
    val => !completedTabs.includes(val) && val !== WalletTransferTab.NATIVE,
  );

  useWalletTransferCompletedStepOnSuccess(transferMutation.status === 'success', WalletTransferTab.NATIVE);
  useEffect(() => {
    return () => {
      if (transferMutation.status === 'success') {
        refetchNativeTokenBalance();
      }
    };
  }, [transferMutation.status, refetchNativeTokenBalance]);

  return (
    <>
      <StepHeader>{t('pages.walletTransfer.sections.nativeToken.header')}</StepHeader>

      <TokenTransferStatusProvider
        queryEnabled={transferEnabled}
        queryStatus={transferMutation.status}
        queryError={transferMutation.error as Error | null}
        queryStart={() => {
          transferMutation.mutate();
        }}
      >
        <div className='space-y-2'>
          <TokenTransferStatusTitle>{t('components.walletTransferTokenInputGroup.native')}</TokenTransferStatusTitle>
          <Title>
            {t('pages.walletTransfer.sections.nativeToken.explanation', {
              tokenName: chain?.name ?? '',
            })}
          </Title>

          <WalletTransferTokenInputGroup
            value={inputValue}
            isAboveBalance={inputAboveBalance}
            onChange={onInputChange}
            onMaxAmountButtonClick={onMaxButtonClick}
            getInitialValue={getInitialInputValue}
          />

          <TokenTransferSingleStepButton />

          {!!skippedSections.length && (
            <NoticeMessage theme={'warning'}>
              {t('pages.walletTransfer.sections.nativeToken.skippedSectionsNotice', {
                skippedSections: skippedSections.join(', '),
              })}
            </NoticeMessage>
          )}

          <TokenTransferStatusContext.Consumer>
            {({error}) => <> {!!error && <NoticeMessageErrorDetails error={error as Error} />}</>}
          </TokenTransferStatusContext.Consumer>
        </div>
      </TokenTransferStatusProvider>
    </>
  );
};
